import { FloodGuard, ScanDeduper, type DedupeConfig } from './dedupe.ts';
import { MAX_SCAN_LENGTH, normalizeScanValue, normalizeSymbology } from './normalize.ts';
import type { ProviderKind, RawScan, RejectReason, ScanEvent, ScanSource, ScannerMode } from './types.ts';

export type ScanDecision =
  | { readonly accepted: true; readonly event: ScanEvent }
  | { readonly accepted: false; readonly reason: RejectReason };

/** One line of the diagnostic history. Carries no barcode characters: only length, symbology and timing. */
export type DecisionRecord = {
  readonly at: number;
  readonly providerId: string;
  readonly source: ScanSource;
  readonly accepted: boolean;
  readonly reason?: RejectReason | undefined;
  readonly length: number;
  readonly symbology?: string | undefined;
  readonly via?: string | undefined;
  readonly maxGapMs?: number | undefined;
  readonly sincePrevMs: number | null;
};

export type ProviderActivity = {
  readonly id: string;
  readonly kind: ProviderKind | 'MANUAL';
  /** Raw transport events seen (key presses, text changes, broadcasts), before any scan decision. */
  readonly rawEvents: number;
  /** Candidate scans handed to the hub. */
  readonly scans: number;
  readonly accepted: number;
  readonly lastAt: number | null;
};

export type HubDiagnostics = {
  readonly mode: ScannerMode;
  readonly received: number;
  readonly accepted: number;
  readonly rejected: Readonly<Record<string, number>>;
  readonly duplicatesSameProvider: number;
  readonly duplicatesCrossProvider: number;
  readonly providers: readonly ProviderActivity[];
  /** Raw transport events by `providerId:label` (key press, change text, submit editing, broadcast...). */
  readonly rawBreakdown: Readonly<Record<string, number>>;
  readonly symbologies: Readonly<Record<string, number>>;
  readonly lastAccepted: { readonly providerId: string; readonly source: ScanSource; readonly profileId?: string; readonly at: number } | null;
  readonly history: readonly DecisionRecord[];
  readonly subscribers: number;
};

export type HubOptions = {
  readonly mode?: ScannerMode;
  readonly dedupe?: Partial<DedupeConfig>;
  readonly maxLength?: number;
  readonly flood?: { readonly maxPerWindow: number; readonly windowMs: number };
  readonly now?: () => number;
  readonly historySize?: number;
};

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * The hub every provider reports into: mode filter -> normalisation -> flood guard -> cross-provider dedupe ->
 * the topmost subscriber. It knows nothing about transports and nothing about warehouse operations.
 */
export class ScanInputHub {
  #mode: ScannerMode;
  readonly #now: () => number;
  readonly #maxLength: number;
  readonly #deduper: ScanDeduper;
  readonly #flood: FloodGuard;
  readonly #floodByProvider = new Map<string, FloodGuard>();
  readonly #floodMax: number;
  readonly #floodWindow: number;
  readonly #historySize: number;
  readonly #subs: Array<{ readonly id: string | undefined; readonly handler: (e: ScanEvent) => void }> = [];
  #raw: Record<string, number> = {};
  readonly #listeners = new Set<() => void>();
  readonly #activity = new Map<string, Mutable<ProviderActivity>>();
  #received = 0;
  #accepted = 0;
  #rejected: Record<string, number> = {};
  #dupSame = 0;
  #dupCross = 0;
  #symbologies: Record<string, number> = {};
  #lastAccepted: HubDiagnostics['lastAccepted'] = null;
  #history: DecisionRecord[] = [];
  #lastDecisionAt: number | null = null;

  constructor(opts: HubOptions = {}) {
    this.#mode = opts.mode ?? 'AUTO';
    this.#now = opts.now ?? Date.now;
    this.#maxLength = opts.maxLength ?? MAX_SCAN_LENGTH;
    this.#deduper = new ScanDeduper(opts.dedupe);
    this.#floodMax = opts.flood?.maxPerWindow ?? 20;
    this.#floodWindow = opts.flood?.windowMs ?? 1000;
    // One noisy provider must not starve another: a per-provider bucket plus a larger global cap.
    this.#flood = new FloodGuard(this.#floodMax * 3, this.#floodWindow);
    this.#historySize = opts.historySize ?? 50;
  }

  get mode(): ScannerMode {
    return this.#mode;
  }

  setMode(mode: ScannerMode): void {
    if (mode === this.#mode) return;
    this.#mode = mode;
    this.#deduper.reset();
    this.#changed();
  }

  setDedupe(cfg: Partial<DedupeConfig>): void {
    this.#deduper.setConfig(cfg);
  }

  /** The topmost subscriber receives scans (a screen that mounts a scan field pushes itself on the stack). */
  subscribe(handler: (e: ScanEvent) => void, id?: string): () => void {
    const entry = { id, handler };
    this.#subs.push(entry);
    this.#changed();
    return () => {
      const i = this.#subs.lastIndexOf(entry);
      if (i >= 0) this.#subs.splice(i, 1);
      this.#changed();
    };
  }

  get subscriberCount(): number {
    return this.#subs.length;
  }

  /** Change notification for UIs (diagnostics, settings). */
  onChange(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /** A provider saw raw transport activity (a key press, a text change, a broadcast) - before any scan decision. */
  noteRawEvent(providerId: string, kind: ProviderKind | 'MANUAL', label?: string): void {
    const a = this.#activityOf(providerId, kind);
    a.rawEvents += 1;
    a.lastAt = this.#now();
    if (label !== undefined) {
      const key = `${providerId}:${label}`;
      this.#raw[key] = (this.#raw[key] ?? 0) + 1;
    }
    this.#changed();
  }

  /** A provider decided something locally (too short, terminator late). Counted, never delivered. */
  reportRejected(providerId: string, kind: ProviderKind | 'MANUAL', reason: RejectReason, length = 0): void {
    const now = this.#now();
    const a = this.#activityOf(providerId, kind);
    a.scans += 1;
    a.lastAt = now;
    this.#received += 1;
    this.#count(reason);
    this.#record({ at: now, providerId, source: kind === 'MANUAL' ? 'MANUAL' : kind, accepted: false, reason, length });
    this.#changed();
  }

  accept(raw: RawScan): ScanDecision {
    const now = this.#now();
    const kind: ProviderKind | 'MANUAL' = raw.source === 'MANUAL' ? 'MANUAL' : raw.source;
    const a = this.#activityOf(raw.providerId, kind);
    a.scans += 1;
    a.lastAt = now;
    this.#received += 1;

    const reject = (reason: RejectReason, length: number, symbology?: string): ScanDecision => {
      this.#count(reason);
      this.#record({ at: now, providerId: raw.providerId, source: raw.source, accepted: false, reason, length, symbology, via: raw.via, maxGapMs: raw.maxGapMs });
      this.#changed();
      return { accepted: false, reason };
    };

    const length = typeof raw.value === 'string' ? raw.value.length : 0;
    if (!this.#modeAllows(raw.source)) return reject('MODE_FILTERED', length);

    const n = normalizeScanValue(raw.value, { maxLength: this.#maxLength });
    if (!n.ok) return reject(n.reason, length);
    const symbology = normalizeSymbology(raw.symbology) ?? n.symbology;
    const manual = raw.source === 'MANUAL';

    if (!manual) {
      let bucket = this.#floodByProvider.get(raw.providerId);
      if (!bucket) {
        bucket = new FloodGuard(this.#floodMax, this.#floodWindow);
        this.#floodByProvider.set(raw.providerId, bucket);
      }
      if (!bucket.allow(now) || !this.#flood.allow(now)) return reject('FLOOD', n.value.length, symbology);
      const verdict = this.#deduper.check({ value: n.value, symbology, providerId: raw.providerId, at: now });
      if (verdict.duplicate) {
        if (verdict.kind === 'SAME_PROVIDER') this.#dupSame += 1;
        else this.#dupCross += 1;
        return reject(verdict.kind === 'SAME_PROVIDER' ? 'DUPLICATE_SAME_PROVIDER' : 'DUPLICATE_CROSS_PROVIDER', n.value.length, symbology);
      }
    }

    const target =
      (raw.targetId !== undefined ? this.#subs.find((s) => s.id === raw.targetId) : undefined) ?? this.#subs[this.#subs.length - 1];
    if (!target) return reject('NO_SUBSCRIBER', n.value.length, symbology);

    const event: ScanEvent = {
      value: n.value,
      ...(symbology !== undefined ? { symbology } : {}),
      timestamp: raw.timestamp ?? now,
      source: raw.source,
      ...(raw.suspect !== undefined ? { suspect: raw.suspect } : {}),
      provider: {
        id: raw.providerId,
        ...(raw.profileId !== undefined ? { profileId: raw.profileId } : {}),
        ...(raw.via !== undefined ? { via: raw.via } : {}),
      },
    };
    this.#accepted += 1;
    a.accepted += 1;
    if (symbology !== undefined) this.#symbologies[symbology] = (this.#symbologies[symbology] ?? 0) + 1;
    this.#lastAccepted = { providerId: raw.providerId, source: raw.source, ...(raw.profileId !== undefined ? { profileId: raw.profileId } : {}), at: now };
    this.#record({ at: now, providerId: raw.providerId, source: raw.source, accepted: true, length: n.value.length, symbology, via: raw.via, maxGapMs: raw.maxGapMs });
    this.#changed();
    target.handler(event);
    return { accepted: true, event };
  }

  get diagnostics(): HubDiagnostics {
    return {
      mode: this.#mode,
      received: this.#received,
      accepted: this.#accepted,
      rejected: { ...this.#rejected },
      duplicatesSameProvider: this.#dupSame,
      duplicatesCrossProvider: this.#dupCross,
      providers: [...this.#activity.values()].map((x) => ({ ...x })),
      rawBreakdown: { ...this.#raw },
      symbologies: { ...this.#symbologies },
      lastAccepted: this.#lastAccepted,
      history: [...this.#history],
      subscribers: this.#subs.length,
    };
  }

  resetDiagnostics(): void {
    this.#received = 0;
    this.#accepted = 0;
    this.#rejected = {};
    this.#dupSame = 0;
    this.#dupCross = 0;
    this.#symbologies = {};
    this.#lastAccepted = null;
    this.#history = [];
    this.#lastDecisionAt = null;
    this.#activity.clear();
    this.#raw = {};
    this.#deduper.reset();
    this.#flood.reset();
    this.#floodByProvider.clear();
    this.#changed();
  }

  #modeAllows(source: ScanSource): boolean {
    if (source === 'MANUAL') return true;
    switch (this.#mode) {
      case 'AUTO':
        return true;
      case 'KEYBOARD':
        return source === 'KEYBOARD';
      case 'ANDROID_BROADCAST':
        return source === 'ANDROID_BROADCAST';
      case 'VENDOR_SDK':
        return source === 'VENDOR_SDK';
    }
  }

  #activityOf(id: string, kind: ProviderKind | 'MANUAL'): Mutable<ProviderActivity> {
    let a = this.#activity.get(id);
    if (!a) {
      a = { id, kind, rawEvents: 0, scans: 0, accepted: 0, lastAt: null };
      this.#activity.set(id, a);
    }
    return a;
  }

  #count(reason: RejectReason): void {
    this.#rejected[reason] = (this.#rejected[reason] ?? 0) + 1;
  }

  #record(r: Omit<DecisionRecord, 'sincePrevMs'>): void {
    const sincePrevMs = this.#lastDecisionAt === null ? null : r.at - this.#lastDecisionAt;
    this.#lastDecisionAt = r.at;
    this.#history = [...this.#history.slice(-(this.#historySize - 1)), { ...r, sincePrevMs }];
  }

  #changed(): void {
    for (const l of this.#listeners) l();
  }
}
